import { jsPDF } from "jspdf";
import "jspdf-autotable";
import JsBarcode from "jsbarcode";
import {
  companyName,
  getLogo,
  isPdf,
  normalizeDocumentDataUrl,
  openJsPdfInNewTab,
  subdivisionName,
} from "../../../services/Functions";
import { pdfToImages } from "../../../utils/pdfToImages";
import { mapDocumentsToReadingRows, resolveMrcTariffDisplay } from "./mrcReadingUtils";
import { mapMrcDocumentsToReceiptReadingRows } from "./printMrcApplicationReceipt";

const SMALL_LOGO_CODES = ["12", "13", "11", "16", "26", "27", "48", "59"];

const resolveDiscoCode = (application = {}) => {
  const fromRef = String(application.referenceNo || "")
    .replace(/\s/g, "")
    .slice(2, 4);
  const code = String(
    application.companyCode ||
      application.discoCode ||
      fromRef ||
      application.subDivisionCode ||
      ""
  ).slice(0, 2);
  return code || "15";
};

const formatDisplayDate = (value) => {
  if (!value) return "-";
  if (typeof value === "string" && /^\d{2}-[A-Za-z]{3}-\d{2,4}$/.test(value)) {
    return value;
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const day = String(d.getDate()).padStart(2, "0");
  return `${day}-${months[d.getMonth()]}-${d.getFullYear()}`;
};

const normalizeSrc = (value) => {
  if (value == null) return "";
  const trimmed = String(value).trim();
  if (
    !trimmed ||
    trimmed.toLowerCase() === "null" ||
    trimmed.toLowerCase() === "undefined"
  ) {
    return "";
  }
  return normalizeDocumentDataUrl(trimmed) || trimmed;
};

/** Flatten labels that contain newlines (e.g. "Import Off-Peak\\nKWH"). */
const cleanDocTitle = (title) =>
  String(title || "Document")
    .replace(/[\r\n]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/** Same document-page header layout as NC / COA E-File. */
const addPageHeader = (doc, discoCode, applicationId, documentTitle) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const padding = 10;
  const logoWidth = SMALL_LOGO_CODES.includes(discoCode) ? 25 : 30;
  const logoHeight = SMALL_LOGO_CODES.includes(discoCode) ? 15 : 8;
  const logoX = (pageWidth - logoWidth) / 2;

  let height = 10;

  doc.addImage(getLogo(discoCode), "PNG", logoX, 5, logoWidth, logoHeight);

  height += 15;
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text(companyName(discoCode), pageWidth / 2, height, { align: "center" });

  height += 10;
  doc.setFont("helvetica", "normal");
  doc.text("Application Id: ", padding, height);
  doc.text(
    String(applicationId || "-"),
    padding + doc.getTextWidth("Application Id: "),
    height
  );

  height += 10;
  const rectWidth = 195;
  const rectHeight = 10;
  const rectX = (pageWidth - rectWidth) / 2;
  const title = cleanDocTitle(documentTitle);

  doc.setFillColor(199, 199, 199);
  doc.rect(rectX, height - 6.5, rectWidth, rectHeight, "DF");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  // Keep title inside the gray bar (shrink if very long)
  const maxTitleWidth = rectWidth - 8;
  let titleSize = 11;
  while (titleSize > 8 && doc.getTextWidth(title) > maxTitleWidth) {
    titleSize -= 0.5;
    doc.setFontSize(titleSize);
  }
  doc.text(title, pageWidth / 2, height, { align: "center" });

  return height + 3.5;
};

const addImageOrPdfPage = async (
  doc,
  discoCode,
  applicationId,
  title,
  src
) => {
  const normalized = normalizeSrc(src);
  if (!normalized) return;

  const pageWidth = doc.internal.pageSize.getWidth();
  const rectWidth = 195;
  const rectHeight = 234;
  const rectX = (pageWidth - rectWidth) / 2;

  const drawFrame = (contentHeight) => {
    doc.rect(rectX, contentHeight, rectWidth, rectHeight);
  };

  if (
    isPdf(normalized) ||
    isPdf(src) ||
    normalized.startsWith("data:application/pdf")
  ) {
    const images = await pdfToImages(normalized);
    if (!images.length) {
      doc.addPage();
      const contentHeight = addPageHeader(
        doc,
        discoCode,
        applicationId,
        title
      );
      drawFrame(contentHeight);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.setTextColor(100, 100, 100);
      doc.text("(PDF attached)", pageWidth / 2, contentHeight + rectHeight / 2, {
        align: "center",
      });
      doc.setTextColor(0, 0, 0);
      return;
    }

    for (let i = 0; i < images.length; i += 1) {
      doc.addPage();
      const contentHeight = addPageHeader(
        doc,
        discoCode,
        applicationId,
        i === 0 ? title : `${title} (Continued)`
      );
      drawFrame(contentHeight);
      const cardWidth = 190;
      const cardHeight = 228;
      const xPos = (pageWidth - cardWidth) / 2;
      doc.addImage(
        images[i],
        "PNG",
        xPos,
        contentHeight + 3,
        cardWidth,
        cardHeight
      );
    }
    return;
  }

  doc.addPage();
  const contentHeight = addPageHeader(doc, discoCode, applicationId, title);
  drawFrame(contentHeight);

  try {
    const body = normalized.includes(",")
      ? normalized.slice(normalized.indexOf(",") + 1).replace(/\s/g, "")
      : normalized.replace(/\s/g, "");
    const format = /^iVBORw0KGgo/i.test(body) ? "PNG" : "JPEG";
    const cardWidth = 190;
    const cardHeight = 228;
    const xPos = (pageWidth - cardWidth) / 2;
    doc.addImage(
      normalized,
      format,
      xPos,
      contentHeight + 3,
      cardWidth,
      cardHeight
    );
  } catch (e) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(150, 60, 60);
    doc.text(
      "(Unable to render document)",
      pageWidth / 2,
      contentHeight + rectHeight / 2,
      { align: "center" }
    );
    doc.setTextColor(0, 0, 0);
  }
};

/**
 * MRC E-File PDF — same E-Document layout as NC / COA:
 * header + barcode, applicant particulars, application summary, document pages.
 */
export const downloadMrcEFilePdf = async ({
  application = {},
  historyRows = [],
  documents = [],
  statusLabel = "",
  subDivisions = [],
  tariffs = [],
  cp52Src = "",
  billSrc = "",
} = {}) => {
  const applicationId = application.applicationId;
  if (!applicationId) {
    throw new Error("Application ID is required for E-File.");
  }

  const discoCode = resolveDiscoCode(application);
  const doc = new jsPDF({ compressPdf: true });
  const pageWidth = doc.internal.pageSize.getWidth();

  const barcodeCanvas = document.createElement("canvas");
  JsBarcode(barcodeCanvas, applicationId);
  const barcodeDataURL = barcodeCanvas.toDataURL("image/png");

  const logoWidth = SMALL_LOGO_CODES.includes(discoCode) ? 25 : 30;
  const logoHeight = SMALL_LOGO_CODES.includes(discoCode) ? 15 : 8;
  const logoX = (pageWidth - logoWidth) / 2;

  doc.addImage(barcodeDataURL, "PNG", pageWidth - 50, 5, 40, 20);
  doc.addImage(getLogo(discoCode), "PNG", logoX, 5, logoWidth, logoHeight);

  let height = 25;
  const padding = 10;
  const leftMargin = 50;

  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text(companyName(discoCode), pageWidth / 2, height, { align: "center" });

  height += 8;
  doc.setFontSize(18);
  doc.text("E-Document", pageWidth / 2, height, { align: "center" });

  height += 16;
  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");

  doc.text("Document Type: ", padding, height);
  doc.text("Meter Reading Correction", padding + leftMargin, height);
  doc.text("Tracking Id:", padding + leftMargin + leftMargin, height);
  doc.text(
    applicationId,
    padding + leftMargin + leftMargin + leftMargin,
    height
  );

  height += 6;
  doc.text("Application Status: ", padding, height);
  doc.text(
    statusLabel || "-",
    padding + leftMargin,
    height
  );
  doc.text("Application Date:", padding + leftMargin + leftMargin, height);
  doc.text(
    formatDisplayDate(application.creationDate),
    padding + leftMargin + leftMargin + leftMargin,
    height
  );

  height += 6;
  const subDivisionCode =
    application.subDivisionCode ||
    application.subdivisionCode ||
    String(application.referenceNo || "").replace(/\s/g, "").slice(2, 7) ||
    "";
  doc.text("Sub-Division: ", padding, height);
  doc.text(
    subdivisionName(parseInt(subDivisionCode, 10), subDivisions) ||
      subDivisionCode ||
      "-",
    padding + leftMargin,
    height
  );
  doc.text("Reference No.:", padding + leftMargin + leftMargin, height);
  doc.text(
    application.referenceNo || "-",
    padding + leftMargin + leftMargin + leftMargin,
    height
  );

  height += 6;
  const tariffDisplay =
    resolveMrcTariffDisplay(
      application.tariffCode ||
        application.tariff ||
        application.TARIFF_ACTIVE ||
        application.tariffDescription ||
        application.tariffDesc ||
        application.tariffName ||
        application.meterPhaseType ||
        "",
      tariffs
    ) || "-";
  doc.text("Meter / Tariff: ", padding, height);
  const tariffLines = doc.splitTextToSize(String(tariffDisplay), 48);
  doc.text(tariffLines[0] || "-", padding + leftMargin, height);
  doc.text("Customer ID:", padding + leftMargin + leftMargin, height);
  doc.text(
    String(
      application.customerId ||
        application.consumerId ||
        application.accountNo ||
        "-"
    ),
    padding + leftMargin + leftMargin + leftMargin,
    height
  );

  height += 20;
  const rectWidth = 195;
  let rectHeight = 10;
  const rectX = (pageWidth - rectWidth) / 2;

  doc.setFillColor(200, 200, 200);
  doc.rect(rectX, height - 6.5, rectWidth, rectHeight, "DF");
  doc.setFont("helvetica", "bold");
  doc.text("Applicant Particulars", pageWidth / 2, height, {
    align: "center",
  });

  height += 6;
  rectHeight = 30;
  doc.rect(rectX, height - 2.5, rectWidth, rectHeight);

  doc.setFont("helvetica", "normal");
  height += 8;
  const extraPadding = 5;

  doc.text("Applicant Name:", padding + extraPadding, height);
  doc.text(
    application.consumerName ||
      application.applicantName ||
      "-",
    padding + 70,
    height
  );

  height += 6;
  doc.text("CNIC:", padding + extraPadding, height);
  doc.text(
    application.applicantCNIC ||
      application.consumerCNIC ||
      application.cnic ||
      "-",
    padding + 70,
    height
  );

  height += 6;
  doc.text("Present Address:", padding + extraPadding, height);
  const address = String(
    application.consumerAddress || application.address || "-"
  );
  const addressLines = doc.splitTextToSize(address, 120);
  doc.text(addressLines[0] || "-", padding + 70, height);

  height += 6;
  doc.text("Phone No.:", padding + extraPadding, height);
  doc.text(
    application.applicantMobile ||
      application.applicantPhoneNo ||
      application.applicantPhone ||
      "-",
    padding + 70,
    height
  );

  height += 15;
  rectHeight = 10;
  doc.setFillColor(199, 199, 199);
  doc.rect(rectX, height - 6.5, rectWidth, rectHeight, "DF");
  doc.setFont("helvetica", "bold");
  doc.text("Application Summary", pageWidth / 2, height, {
    align: "center",
  });

  doc.setFontSize(12);
  height += 3.5;

  const summaryData = [];
  if (Array.isArray(historyRows) && historyRows.length) {
    const sorted = [...historyRows];
    let sr = sorted.length;
    sorted.forEach((row) => {
      const dateRaw = row.date || row.lastModificationDate || "";
      const dateText =
        typeof dateRaw === "string" && dateRaw.includes(" ")
          ? dateRaw.slice(0, 11)
          : formatDisplayDate(dateRaw).slice(0, 11);
      summaryData.push([
        String(sr),
        row.stepName || "-",
        row.processedBy || "-",
        dateText || "-",
        row.remarks || "-",
      ]);
      sr -= 1;
    });
  }

  doc.autoTable({
    head: [["Sr.", "Step", "Processed By", "Date", "Remarks"]],
    body: summaryData,
    margin: { left: rectX, right: rectX },
    startY: height,
    tableWidth: rectWidth,
    styles: {
      fontSize: 11,
      fontStyle: "normal",
      halign: "center",
      valign: "middle",
      lineWidth: 0,
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      minCellHeight: 10,
    },
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontSize: 11,
      fontStyle: "bold",
      halign: "center",
      valign: "middle",
      lineWidth: 0,
    },
    alternateRowStyles: {
      fillColor: [235, 235, 235],
    },
    columnStyles: {
      0: { halign: "center", cellWidth: 15 },
      1: { halign: "center", cellWidth: 40 },
      2: { halign: "center", cellWidth: 35 },
      3: { halign: "center", cellWidth: 30 },
      4: { halign: "center", cellWidth: 65 },
    },
    theme: "plain",
    tableLineWidth: 0.5,
    tableLineColor: [0, 0, 0],
  });

  // Meter Reading Details — after Application Summary (activity log)
  const formatReadingCell = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    const n = Number(value);
    return Number.isFinite(n) ? String(n) : String(value).trim() || "-";
  };
  const hasReadingValue = (value) =>
    value !== null && value !== undefined && String(value).trim() !== "";

  const meterPhaseType = String(application.meterPhaseType || "");
  const receiptReadingRows = mapMrcDocumentsToReceiptReadingRows(documents, {
    meterPhaseType,
  }).filter(
    (row) =>
      hasReadingValue(row.previousReading) ||
      hasReadingValue(row.asChargedReading) ||
      hasReadingValue(row.claimedReading) ||
      hasReadingValue(row.authorityReading) ||
      hasReadingValue(row.siteSurveyReading) ||
      row.serialNo
  );

  if (receiptReadingRows.length) {
    const showSiteSurveyCol = receiptReadingRows.some((row) =>
      hasReadingValue(row.siteSurveyReading)
    );
    const showAuthorityCol = receiptReadingRows.some((row) =>
      hasReadingValue(row.authorityReading)
    );

    const readingHead = [
      "Meter Type",
      "Serial No.",
      "Previous Reading",
      "As Charged Reading",
      "User Claimed Reading",
    ];
    if (showSiteSurveyCol) readingHead.push("Site Survey Reading");
    if (showAuthorityCol) readingHead.push("Authority Reading");

    const readingBody = receiptReadingRows.map((row) => {
      const cells = [
        cleanDocTitle(row.meterType || "-") || "-",
        String(row.serialNo || "").trim() || "-",
        formatReadingCell(row.previousReading),
        formatReadingCell(row.asChargedReading),
        formatReadingCell(row.claimedReading),
      ];
      if (showSiteSurveyCol) {
        cells.push(formatReadingCell(row.siteSurveyReading));
      }
      if (showAuthorityCol) {
        cells.push(formatReadingCell(row.authorityReading));
      }
      return cells;
    });

    let meterTableStartY = (doc.lastAutoTable?.finalY || height) + 12;
    const pageHeight = doc.internal.pageSize.getHeight();
    if (meterTableStartY > pageHeight - 50) {
      doc.addPage();
      meterTableStartY = 20;
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("Meter Reading Details:", padding, meterTableStartY);

    doc.autoTable({
      startY: meterTableStartY + 4,
      head: [readingHead],
      body: readingBody,
      theme: "grid",
      styles: {
        fontSize: readingHead.length > 5 ? 7 : 8,
        halign: "center",
        valign: "middle",
        overflow: "linebreak",
        cellPadding: 1.2,
      },
      headStyles: {
        fillColor: [66, 66, 66],
        textColor: 255,
        fontStyle: "bold",
        halign: "center",
      },
      columnStyles: {
        0: { halign: "left", cellWidth: 42, fontStyle: "bold" },
      },
      margin: { left: padding, right: padding },
      tableWidth: pageWidth - padding * 2,
    });
  }

  const readingRows = mapDocumentsToReadingRows(documents, {
    meterPhaseType: application.meterPhaseType,
  });

  for (const row of readingRows) {
    const meterName = cleanDocTitle(row.meterLabel || "Meter");
    const setLabel =
      row.setNo != null && row.setNo !== "" ? ` — Set ${row.setNo}` : "";
    if (row.imageSrc) {
      await addImageOrPdfPage(
        doc,
        discoCode,
        applicationId,
        `${meterName}${setLabel} — Claimed Evidence`,
        row.imageSrc
      );
    }
    if (row.siteSurveyImageSrc) {
      await addImageOrPdfPage(
        doc,
        discoCode,
        applicationId,
        `${meterName}${setLabel} — Site Evidence`,
        row.siteSurveyImageSrc
      );
    }
  }

  if (cp52Src) {
    await addImageOrPdfPage(
      doc,
      discoCode,
      applicationId,
      "CP Form 52",
      cp52Src
    );
  }

  if (billSrc) {
    await addImageOrPdfPage(
      doc,
      discoCode,
      applicationId,
      "Electricity Consumer Bill",
      billSrc
    );
  }

  openJsPdfInNewTab(doc, `${applicationId}-E-File.pdf`);
};

export default downloadMrcEFilePdf;
